import { useCallback, useState, type CSSProperties } from "react";
import type { MediaStreamLike } from "@cuekards/ui";
import type {
  CaptureFormat,
  CaptureKind,
  DeviceState,
  ResolvedDevice,
} from "@cuekards/core/media-devices";
import {
  CAPTURE_KINDS,
  CAPTURE_KIND_NOUN,
  FRAME_RATE_PRESETS,
  RESOLUTION_PRESETS,
  displayLabel,
  selectableDevices,
} from "@cuekards/core/media-devices";
import { CameraOffIcon } from "./icons.js";
import { useFrameAspect, useSelfView } from "./self-view.js";
import { useDevices } from "./use-devices.js";

/**
 * Which camera and microphone a take is shot with (FR-41/FR-42) — the web's
 * device screen.
 *
 * It is the one part of the page that can *see* the hardware:
 * `navigator.mediaDevices` lives where a document does, and the choice lives in
 * the core worker with the rest of application state. So this enumerates and
 * reports, and everything it draws comes back as a {@link DeviceState} the
 * worker resolved — including the notice raised when a remembered device is no
 * longer there.
 *
 * Deliberately not the desktop's `DevicePicker`, and not for want of trying to
 * share it. That component ends with the output folder and a Reveal button, and
 * a tab has neither; its permission copy names a Windows settings path, where a
 * browser's answer is its own address-bar prompt. What is genuinely shared — what
 * a device is called, which is the system default, what a stale choice resolves
 * to, what to say about it — is in `@cuekards/core/media-devices` and is used by
 * both.
 *
 * ## Hot-plug, and the one thing this must not do
 *
 * Every enumeration is a report, so a camera arriving or leaving needs no reload:
 * `devicechange` fires, this re-enumerates, the worker re-resolves, and a device
 * that has gone becomes a notice rather than a failure when record is pressed.
 *
 * What it must not do is enumerate by opening the hardware. Until a page has been
 * granted media permission a browser returns placeholders with blank labels, and
 * the way to get real names is to open a device once. That open is **asked for**
 * rather than taken: a page that turns your camera on because you opened its
 * settings is a page nobody should trust, and — FR-31 — every open of the
 * microphone that is not the recording engine's is one that has to justify
 * itself. This one is a button, it happens at most once, and the device is handed
 * straight back. Nothing during a take opens anything but `CaptureEngine`.
 *
 * **The preview is handed over, never opened** (web-ui ticket 09), and that is
 * the same rule seen from its most tempting angle: the obvious way to show the
 * selected camera on a device screen is to open it, and doing so here would put
 * a second `getUserMedia` a few lines under the paragraph explaining why there
 * is only one. It is the host's `useCapture` stream instead — the same object
 * the green room's stage is drawing — so switching camera closes and reopens one
 * device in one engine rather than two in two.
 */
export function DeviceSettings({
  stream,
  showing,
  disabled = false,
}: {
  /** The host's preview stream, or null when there is nothing open. */
  stream: MediaStreamLike | null;
  /** Whether it carries a picture, as against merely a microphone. */
  showing: boolean;
  disabled?: boolean;
}): JSX.Element {
  // Enumerating, reporting and following are {@link useDevices} since web-ui
  // ticket 05, because the green room needs the same three things — and needs
  // them to produce the *same* `DeviceState` this screen does, or a take will
  // not borrow the camera the room already has open.
  const { state, needsPermission, enumerate } = useDevices();
  const [refused, setRefused] = useState(false);

  const grant = useCallback(async (): Promise<void> => {
    setRefused(!(await requestMediaPermission()));
    await enumerate();
  }, [enumerate]);

  const setFormat = useCallback(
    (patch: CaptureFormat) => {
      // The worker sanitises and treats "nothing usable" as native, so a cleared
      // dropdown needs no special case here.
      window.cuekards.setCaptureFormat({ ...state?.selection.format, ...patch });
    },
    [state],
  );

  if (!state) return <section className="settings-screen" aria-busy="true" />;

  const format = state.selection.format;
  const resolution =
    format?.width && format.height ? `${format.width}x${format.height}` : "";
  const overridden =
    state.selection.choices !== undefined || state.selection.format !== undefined;

  return (
    <section className="settings-screen devices">
      <p className="settings-note">
        What a take is shot with. Plugging something in or pulling it out shows up
        here without reloading.
      </p>

      {/* The picture, and the two settings that decide what it is made of
        * (web-ui ticket 09). A device picker without one describes the result
        * instead of showing it, which is the entire reason they are hard to use
        * — and the format selects are the two rows whose effect is genuinely
        * invisible in a dropdown, so they are the two that stand beside it. */}
      <div className="web-device-preview">
        <SelectedCamera stream={stream} showing={showing} />

        <div className="web-device-format">
          <label className="device-row">
            <span>Resolution</span>
            <select
              value={resolution}
              disabled={disabled}
              onChange={(event) => {
                const [w, h] = event.target.value.split("x");
                setFormat({
                  width: w ? Number(w) : undefined,
                  height: h ? Number(h) : undefined,
                });
              }}
            >
              <option value="">Native (device default)</option>
              {RESOLUTION_PRESETS.map((preset) => (
                <option key={preset.label} value={`${preset.width}x${preset.height}`}>
                  {preset.label}
                </option>
              ))}
            </select>
          </label>

          <label className="device-row">
            <span>Framerate</span>
            <select
              value={format?.frameRate ?? ""}
              disabled={disabled}
              onChange={(event) =>
                setFormat({
                  frameRate: event.target.value
                    ? Number(event.target.value)
                    : undefined,
                })
              }
            >
              <option value="">Native (device default)</option>
              {FRAME_RATE_PRESETS.map((fps) => (
                <option key={fps} value={fps}>
                  {fps} fps
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {CAPTURE_KINDS.map((kind) => (
        <DeviceSelect
          key={kind}
          kind={kind}
          state={state}
          resolved={state.resolved[kind]}
          disabled={disabled}
        />
      ))}

      {!state.enumerated && <p className="device-status">Looking for devices…</p>}

      {/* Asked for rather than taken — see the note at the top. It says what it
        * is going to do, because "Allow" on a page that has not explained itself
        * is the prompt people dismiss. */}
      {needsPermission && (
        <p className="device-notice" role="status">
          Your browser is hiding the names of your devices until cuekards has been
          allowed to use them.{" "}
          <button
            type="button"
            className="device-grant"
            onClick={() => void grant()}
            disabled={disabled}
            title="Opens the camera and microphone once, and lets go of them straight away"
          >
            Show device names
          </button>
        </p>
      )}

      {refused && (
        <p className="device-notice" role="status">
          Permission was refused, so the names stay hidden. You can still record —
          cuekards will ask again when you start a take — or grant it from the
          padlock beside the address bar.
        </p>
      )}

      {state.notices.map((notice) => (
        <p className="device-notice" role="status" key={notice}>
          {notice}
        </p>
      ))}

      <p className="settings-note">
        Takes are never uploaded. They are written to this browser&rsquo;s private
        storage as you record, and saved where you choose when you stop.
      </p>

      <div className="settings-actions">
        {/* FR-64. Back to the system's own camera and microphone at whatever
          * resolution they run natively, which is what cuekards does before you
          * choose anything. */}
        <button
          type="button"
          className="settings-reset"
          onClick={() => window.cuekards.resetDevices()}
          disabled={disabled || !overridden}
          title="Use the system devices at their native format"
        >
          Reset to defaults
        </button>
      </div>
    </section>
  );
}

/**
 * The 16:9 thumbnail at the top of the section: what the selected camera is
 * actually seeing.
 *
 * **A glyph and nothing else when there is no picture**, and the omission is the
 * decision. There are three ways to arrive at an empty well — the camera
 * switched off on the control bar, permission not given, no camera on the
 * machine — and the green room draws all three with a sentence and, for two of
 * them, a button. Repeating that here would make this a second green room in a
 * device picker: the same offer in the wrong place, a second copy of the copy to
 * keep in step, and a "Turn on camera and mic" on the screen you opened to
 * choose which camera. So the room keeps the argument and this keeps the result.
 *
 * At the camera's own ratio rather than at a fixed 16:9, for the reason a device
 * picker exists at all: this is the one place you come to find out what a camera
 * looks like, and a well that reshapes every picture to the same rectangle answers
 * that question wrongly for any camera that is not one. See `useFrameAspect`.
 *
 * Mirrored, like the other two self-views, and the recording is not: the flip is
 * this element's CSS transform and the stream handed to `MediaRecorder` is the
 * same object with nothing between it and the recorder.
 */
function SelectedCamera({
  stream,
  showing,
}: {
  stream: MediaStreamLike | null;
  showing: boolean;
}): JSX.Element {
  const video = useSelfView(stream);
  const aspect = useFrameAspect(video);

  return (
    <div
      className="web-device-shot"
      style={
        showing && aspect !== null
          ? ({ "--frame": `${aspect}` } as CSSProperties)
          : undefined
      }
    >
      <video
        className="web-device-video"
        ref={video}
        muted
        playsInline
        // A picture of you, not a monitor of the take: its own audio would be
        // your voice a fraction of a second late, in front of a microphone.
        autoPlay
      />
      {!showing && (
        <span className="web-device-blank" aria-hidden="true">
          <CameraOffIcon />
        </span>
      )}
    </div>
  );
}

/** One device dropdown: the system default, every device of the kind, and — when
 * the remembered choice has been unplugged — that choice too, so the select still
 * shows what was asked for beside the notice explaining the stand-in. */
function DeviceSelect({
  kind,
  state,
  resolved,
  disabled,
}: {
  kind: CaptureKind;
  state: DeviceState;
  resolved: ResolvedDevice;
  disabled: boolean;
}): JSX.Element {
  const noun = CAPTURE_KIND_NOUN[kind];
  const title = `${noun[0]!.toUpperCase()}${noun.slice(1)}`;
  const missing = resolved.fallback ? resolved.requested : undefined;

  return (
    <label className="device-row">
      <span>{title}</span>
      <select
        value={resolved.requested?.deviceId ?? ""}
        disabled={disabled}
        aria-label={title}
        onChange={(event) =>
          window.cuekards.selectDevice(kind, event.target.value || null)
        }
      >
        <option value="">
          {resolved.systemDefault
            ? `System default (${displayLabel(resolved.systemDefault)})`
            : `System default (no ${noun} found)`}
        </option>
        {selectableDevices(state.devices, kind).map((device) => (
          <option key={device.deviceId} value={device.deviceId}>
            {device.label}
          </option>
        ))}
        {missing && (
          <option value={missing.deviceId}>{missing.label} (unplugged)</option>
        )}
      </select>
    </label>
  );
}

/**
 * Grant this origin media permission so devices come back named, and hand the
 * hardware straight back.
 *
 * The only `getUserMedia` in cuekards that is not `CaptureEngine`'s, and it is here
 * rather than anywhere nearer a take on purpose: it runs from a button on a
 * settings screen, at most once, before anything is rolling, and it releases the
 * tracks in the same turn. Nothing during a take opens a device but the engine,
 * which is what FR-31's invariant is actually about.
 */
async function requestMediaPermission(): Promise<boolean> {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: true,
      audio: true,
    });
    for (const track of stream.getTracks()) track.stop();
    return true;
  } catch {
    return false;
  }
}
